import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem } from '@mui/material';
import { sv } from 'date-fns/locale';
import { MouseEvent, SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TrashIcon } from 'assets/icons/containerActions/trash.svg';
import { ReactComponent as RequestIcon } from 'assets/icons/request.svg';
import { DEFAULT_DATE_FORMAT } from 'constants/dateFormats';
import { theme } from 'styles';
import { Equipment, EquipmentsTableFields } from 'types/equipments';
import { formattedDate } from 'utils/dateHelpers';

import { categoryOptions } from '../../constants';
import { EquipmentAttachments } from '../../EquipmentAttachements';
import { DotsButton, MenuItemContent, TitleCategory, TitleName } from './styled';

interface RowMenuProps {
  onConfirm?: (e: SyntheticEvent) => void;
  onDelete?: (e: SyntheticEvent) => void;
}

function RequestedEquipmentRowMenu({ onConfirm, onDelete }: RowMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { t } = useTranslation();

  if (!onConfirm && !onDelete) return null;

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e: SyntheticEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleConfirm = (e: SyntheticEvent) => {
    handleClose(e);
    onConfirm?.(e);
  };

  const handleDelete = (e: SyntheticEvent) => {
    handleClose(e);
    onDelete?.(e);
  };

  return (
    <>
      <DotsButton onClick={handleOpen}>
        <MoreVertIcon />
      </DotsButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 180,
            padding: '8px',
            borderRadius: '10px',
            border: '1px solid #ddd',
            boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
          },
        }}
        MenuListProps={{ disablePadding: true }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {onConfirm && (
          <MenuItem
            onClick={handleConfirm}
            sx={{
              borderRadius: '6px',
              height: '44px',
              gap: '10px',
              '&:hover': { backgroundColor: theme.palette.white.p100 },
            }}
          >
            <MenuItemContent>
              <RequestIcon style={{ opacity: 0.7, flexShrink: 0 }} />
              {t('equipments.actions.confirm')}
            </MenuItemContent>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem
            onClick={handleDelete}
            sx={{
              borderRadius: '6px',
              height: '44px',
              gap: '10px',
              '&:hover': { backgroundColor: theme.palette.white.p100 },
            }}
          >
            <MenuItemContent>
              <TrashIcon style={{ opacity: 0.7, flexShrink: 0 }} />
              {t('equipments.actions.delete')}
            </MenuItemContent>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

interface RegisteredEquipmentAdapterProps {
  isManager: boolean;
  currentUserId?: string;
  data: Equipment[];
  isSwedish: boolean;
  onConfirmIconClick: ({ id, userId }: Pick<Equipment, 'id' | 'userId'>) => Promise<void>;
  onTrashIconClick: ({ id, name }: Pick<Equipment, 'id' | 'name'>) => void;
  onShowMoreAttachments: ({ id, userId }: Pick<Equipment, 'id' | 'userId'>) => void;
  getIsExpanded: (id: string) => boolean;
  setIsExpanded: (id: string) => void;
}

export function requestedEquipmentAdapter({
  isManager,
  currentUserId,
  data,
  isSwedish,
  onConfirmIconClick,
  onTrashIconClick,
  onShowMoreAttachments,
}: RegisteredEquipmentAdapterProps): EquipmentsTableFields[] {
  if (!data || data.length === 0) {
    return [];
  }

  const locales = isSwedish ? sv : undefined;

  const categorySubTitle = Object.fromEntries(
    categoryOptions.map(({ value, label }) => [value, label]),
  );

  return data.map(
    ({
      id,
      name,
      category,
      userId,
      assignedToId = '',
      expireAt,
      orderedAt,
      attachments,
      serial,
      isEditable,
      authorId,
    }) => {
      const isCurrentAuthor = currentUserId === authorId;
      const isCurrentAssignee = currentUserId === userId;
      const isEditableByManager =
        isManager &&
        ((isCurrentAuthor && isCurrentAssignee && isEditable) ||
          (isCurrentAuthor && !isCurrentAssignee) ||
          (!isCurrentAuthor && !isCurrentAssignee));
      const isConfirmDisabled = !isCurrentAssignee && isManager && currentUserId !== assignedToId;

      const handleConfirmIconClick = (e: SyntheticEvent) => {
        e.stopPropagation();
        onConfirmIconClick({ userId, id });
      };

      const handleTrashIconClick = (e: SyntheticEvent) => {
        e.stopPropagation();
        onTrashIconClick({ id, name });
      };

      const showConfirm = !isConfirmDisabled;
      const showDelete = isEditable || isEditableByManager;

      return {
        name: (
          <>
            <TitleName>{name}</TitleName>
            <TitleCategory>{categorySubTitle[category]}</TitleCategory>
          </>
        ),
        expiryDate: expireAt ? formattedDate(new Date(expireAt), DEFAULT_DATE_FORMAT, locales) : '',
        orderedOn: orderedAt
          ? formattedDate(new Date(orderedAt), DEFAULT_DATE_FORMAT, locales)
          : '',
        attachments: (
          <EquipmentAttachments
            attachments={attachments}
            onShowMore={() => onShowMoreAttachments({ id, userId })}
          />
        ),
        serial,
        actions: (
          <RequestedEquipmentRowMenu
            onConfirm={showConfirm ? handleConfirmIconClick : undefined}
            onDelete={showDelete ? handleTrashIconClick : undefined}
          />
        ),
        isClickable: true,
        userId,
        id,
      };
    },
  );
}
